import React, { useState, useEffect } from 'react';
import { VStack, HStack, Pressable, Button, ScrollView } from 'native-base';
import { Caption, H1 } from '~/shared/Typography';
import Section from '~/shared/Section';
import { SpIcon } from '~/shared/Icon';
import {
    faChevronCircleDown,
    faChevronCircleUp,
    faBell
} from '@fortawesome/free-solid-svg-icons';
import '~/shared/animations/animations.css';
import { useGetHomePage, useI18n, useSpringBigStore, useStashboards } from '@springbig/shared';
import { useHomePageStyles } from '~/shared/styles';
import { Subscriptions } from './components/Subscriptions';
import { GiftCards } from './components/GiftCards';
import { PointBalance } from '~/pages/homePage/components/PointBalance';
import { Referrals } from '~/pages/homePage/components/Referrals';
import { Milestones } from '~/pages/homePage/components/Milestones';
import { RewardsOffers } from '~/pages/homePage/components/RewardsOffers';
import { Messages } from '~/pages/homePage/components/Messages';
import useAnimate from '~/shared/animations/useAnimate';

export const HomePage = () => {

    const { t } = useI18n();
    const { data } = useGetHomePage();
    const { section_background, icon, pressable_text } = useHomePageStyles();
    const { member, merchant, stashboards } = useSpringBigStore();
    const [tries, setTries] = useState(0);

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [cards, setCards] = useState([]);

    const { fetch } = useStashboards();

    useEffect(() => {

        if (merchant && member && !stashboards.length && tries < 3) {
            setTries(tries + 1);
            fetch();
        }
    }, [merchant, member, stashboards, tries]);

    const moveItemUp = (index: number): void => {

        if (index > 0 && index < cards.length) {
            const newCards = [...cards.filter(c => !!c)];
            const temp = newCards[index];
            newCards[index] = newCards[index - 1];
            newCards[index - 1] = temp;
            setCards(newCards);
        }
    };

    const moveItemDown = (index: number): void => {

        if (index >= 0 && index < cards.length - 1) {
            const newCards = [...cards.filter(c => !!c)];
            const temp = newCards[index];
            newCards[index] = newCards[index + 1];
            newCards[index + 1] = temp;
            setCards(newCards);
        }
    };

    const saveOrder = (): void => {

        try {
            const cardKeys = cards.map((card) => card && card.key);
            localStorage.setItem('cardsOrder', JSON.stringify(cardKeys));
            setIsEditing(false);
        }
        catch (error) {
            console.error('Failed to save order', error);
        }
    };

    const loadOrder = (_cards: Array<unknown>): void => {

        const storedOrder = localStorage.getItem('cardsOrder');
        if (storedOrder && JSON.parse(storedOrder).length === _cards.length) {
            const cardKeys = JSON.parse(storedOrder);
            const reorderedCards = cardKeys.map((key: string) =>

                _cards.find(card => card && card.key === key)
            );
            setCards(reorderedCards);
        }
        else {
            setCards(_cards);
        }
    };

    useEffect(() => {

        if (merchant && member && data) {
            const _cards = [
                ( data.subscription_plans && data.subscription_plans.length > 0 &&
                    <Subscriptions key={0} data={data.subscription_plans} t={t} />
                ),
                ( data.gift_cards &&
                    <GiftCards key={1} data={data.gift_cards} t={t} />
                ),
                ( data.point_balance && (
                    data.point_balance.balance > 0 ||
                    data.point_balance.last_points_earned > 0 ||
                    data.point_balance.bonus_offer
                ) &&
                    <PointBalance key={2} data={data.point_balance} t={t} />
                ),
                ( member.referrals.active && data.referrals &&
                    <Referrals key={3} data={data.referrals} t={t} />
                ),
                ( data.milestones &&
                    <Milestones key={4} data={data.milestones} t={t} />
                ),
                ( (data.rewards_offers?.highest_redeemable_reward || data.rewards_offers?.latest_offer) &&
                    <RewardsOffers key={5} data={data.rewards_offers} t={t} />
                ),
                ( stashboards && stashboards.length > 0 &&
                    <Messages
                        key={6}
                        data={
                            [...stashboards].sort((a, b) => a.sent_at - b.sent_at).splice(0, 2)
                        }
                        t={t} />
                ),
            ];

            loadOrder(_cards.filter(Boolean));

        }
    }, [t, stashboards, data, merchant, member]);

    const { animation } = useAnimate(
        {
            keyframes: 'rock',
            duration: '.3s',
            originX: 'center',
            originY: 'top',
        }
    );

    return (
        <Section flex={1}>
            { member && merchant &&
                <ScrollView>
                    <HStack justifyContent={'space-between'} alignItems={'center'} px={2}>
                        { member && member.first_name ?
                            <H1 m={0} fontWeight={'300'}>
                                { t('home_page.named_greetings') }
                                <Caption fontWeight={'500'} fontSize={18}>{ member?.first_name }</Caption>
                                <Caption fontWeight={'500'} fontSize={18}> { member?.last_name }</Caption>
                            </H1>
                            :
                            <H1 m={0} fontWeight={'300'}>
                                { t('home_page.anonymous_greetings') }
                            </H1>
                        }
                        {/* <SpIcon icon={faBell} size={24} /> */}
                    </HStack>
                    <VStack space={4} pb={4} px={2}>
                        <Button p={0} onPress={() => (isEditing ? saveOrder() : setIsEditing(!isEditing))}>
                            <Caption color={pressable_text}>{ isEditing ? 'save' : 'edit' }</Caption>
                        </Button>
                        { data && cards.filter(card => !!card).map((card, index) =>

                            <HStack key={index}>
                                <VStack
                                    space={4}
                                    bg={section_background}
                                    rounded={'2xl'}
                                    py={4}
                                    px={2}
                                    flex={1}
                                    style={ isEditing ? animation : null }
                                >
                                    { card }
                                </VStack>
                                { isEditing &&
                                    <VStack space={4} pl={2} pt={2}>
                                        <Pressable onPress={() => moveItemUp(index)}>
                                            <SpIcon
                                                size={20}
                                                icon={faChevronCircleUp}
                                                color={icon}
                                            />
                                        </Pressable>
                                        <Pressable onPress={() => moveItemDown(index)}>
                                            <SpIcon
                                                size={20}
                                                icon={faChevronCircleDown}
                                                color={icon}
                                            />
                                        </Pressable>
                                    </VStack>
                                }
                            </HStack>
                        )}
                    </VStack>
                </ScrollView>
            }
        </Section>
    );
};
